import React, { memo, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { HrSimulation } from 'routes/HumanResourcesModule/Simulation/helpers/types';
import { StyHrDataGrid } from 'routes/HumanResourcesModule/components/HrDataGrid/HrDataGrid';
import { useQueryClient } from '@tanstack/react-query';
import {
    AGENT_LAST_NAME,
    ORG_CHART_HR_MAPPING,
} from 'routes/HumanResourcesModule/business/definitions';
import _ from 'lodash';
import { DEFAULT_GRID_PROPS } from 'routes/HumanResourcesModule/components/HrDataGrid/helpers/gridHelpers';
import { slugify } from 'helpers/formatHelper';
import { RecurrenceEnum } from 'routes/HumanResourcesModule/Settings/HrPositionMapping/helpers/constants';
import { useHypothesesChangesContext } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/components/contexts/HypothesesChangesContext';
import {
    TRACKABLE_EVENTS,
    TRACKABLE_EVENTS_METADATA,
    trackEventPRH,
} from 'helpers/trackerHelpers';
import { useGetAgentDepartureHypotheses } from './components/Departure/helpers/hooks/agentDepartureHypothesis';
import {
    invalidateSimulationAgentDataQueries,
    useGetHrSimulationAgentData,
} from './helpers/hooks/agentsSimulation';
import { useGetAgentArrivalHypotheses } from './components/Arrival/helpers/hooks/agentArrivalHypothesis';
import UpdateAgentArrivalHypothesisCellRenderer from './components/Arrival/ArrivalCellRenderer/UpdateAgentArrivalHypothesisCellRenderer';
import { getColDefs } from './helpers/colDefinitions';
import { ApplyChangesButton } from '../components/ApplyChangesButton';
import {
    getDefaultAgentGridExportParams,
    getObsoleteGridProps,
} from './helpers/gridHelpers';
import AddAgentButton from './components/AddHypotheses/AddAgent/AddAgent';
import { useSemiTreatmentOfSimulation } from '../../helpers/hooks/semiTreatment';
import AgentHypothesisCellRenderer from './components/cellRenderers/AgentHypothesisCellRenderer/AgentHypothesisCellRenderer';
import { resetAgentQueries } from './components/PayComponent/helpers/valueByDateHelpers';
import { useGetAgentPayComponentHypotheses } from './components/PayComponent/helpers/hooks/agentPayComponentHypothesis';
import { useGetGradeAdvancementHypotheses } from './components/GradeAdvancement/helpers/hooks/gradeAdvancement';
import { useDeepMemo } from 'hooks/helpers';
import { GridOptions } from 'ag-grid-community';
import { AgentDataGridContext } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/types';
import HistoricalOrgChartHrContext from 'routes/HumanResourcesModule/Simulation/contexts/ObsoleteOrgChartSimulationContext';

type Props = {
    hrSimulation: HrSimulation;
    recurrence: RecurrenceEnum;
};

const AgentDataGrid = ({
    hrSimulation,
    recurrence,
}: Props): React.ReactElement => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { id: simulationId, title } = hrSimulation;
    const { hasChanges, setHasChanges } = useHypothesesChangesContext();

    const {
        data: hrSimulationAgentsDataResponse,
        isLoading: isHrSimulationAgentsResponseLoading,
        isFetching: isHrSimulationAgentsResponseFetching,
    } = useGetHrSimulationAgentData(simulationId, recurrence);
    const { data: obsoleteOrgChartHr } = useContext(
        HistoricalOrgChartHrContext,
    );
    const {
        data: agentDepartureHypothesesResponse,
        isLoading: isAgentDepartureHypothesesResponseLoading,
        isFetching: isAgentDepartureHypothesesResponseFetching,
    } = useGetAgentDepartureHypotheses(simulationId);

    const agentDepartureHypothesesData = agentDepartureHypothesesResponse || [];

    const {
        data: agentArrivalHypothesesResponse,
        isLoading: isAgentArrivalHypothesesResponseLoading,
        isFetching: isAgentArrivalHypothesesResponseFetching,
    } = useGetAgentArrivalHypotheses(simulationId);

    const agentArrivalHypothesesData = agentArrivalHypothesesResponse || [];

    const {
        data: agentPayComponentHypothesesResponse,
        isLoading: isAgentPayComponentHypothesesResponseLoading,
        isFetching: isAgentPayComponentHypothesesResponseFetching,
    } = useGetAgentPayComponentHypotheses(simulationId);

    const agentPayComponentHypothesesData =
        agentPayComponentHypothesesResponse || [];

    const {
        data: gradeAdvancementHypothesesResponse,
        isLoading: isGradeAdvancementHypothesesResponseLoading,
        isFetching: isGradeAdvancementHypothesesResponseFetching,
    } = useGetGradeAdvancementHypotheses(simulationId);

    const gradeAdvancementHypothesesData =
        gradeAdvancementHypothesesResponse || [];

    const agentData = useMemo(
        () =>
            _.sortBy(hrSimulationAgentsDataResponse?.data, [
                ORG_CHART_HR_MAPPING,
                AGENT_LAST_NAME,
            ]) || [],
        [hrSimulationAgentsDataResponse?.data],
    );

    const { data: semiTreatmentHypothesesData = [] } =
        useSemiTreatmentOfSimulation(hrSimulation);

    // this memoization prevents the grid from re-rendering when the data changes
    const columnDefs = useDeepMemo(
        () =>
            getColDefs({
                hrSimulation,
                recurrence,
                semiTreatmentHypothesesData,
            }),
        [hrSimulation, recurrence, semiTreatmentHypothesesData],
    );

    const obsoleteGridProps = getObsoleteGridProps({
        hrSimulation,
        obsoleteOrgChartHr,
    });
    const exportFileName = t(
        'humanResources.simulation.simulationAgentsDataFileName',
        {
            title: slugify(title),
            recurrence,
        },
    );
    const gridProps: GridOptions = {
        ...obsoleteGridProps,
        components: {
            ...DEFAULT_GRID_PROPS.components,
            agentHypothesisCellRenderer: AgentHypothesisCellRenderer,
            updateAgentArrivalHypothesisCellRenderer:
                UpdateAgentArrivalHypothesisCellRenderer,
        },
        defaultExcelExportParams: getDefaultAgentGridExportParams(recurrence),
        defaultCsvExportParams: getDefaultAgentGridExportParams(recurrence),
        context: {
            agentArrivalHypothesesData,
            agentDepartureHypothesesData,
            agentPayComponentHypothesesData,
            gradeAdvancementHypothesesData,
        } as AgentDataGridContext,
    };

    const reloadAgentsData = () => {
        resetAgentQueries(queryClient);
        invalidateSimulationAgentDataQueries(
            simulationId,
            queryClient,
            recurrence,
        );
        setHasChanges(false);
        const eventName =
            recurrence === RecurrenceEnum.RECURRENT
                ? TRACKABLE_EVENTS.human_resources.simulation.recurrent
                      .dataRefresh
                : TRACKABLE_EVENTS.human_resources.simulation.nonRecurrent
                      .agentDataRefresh;
        trackEventPRH(eventName);
    };

    const headerActions = (
        <ApplyChangesButton
            isLoading={isHrSimulationAgentsResponseFetching}
            isDisabled={hasChanges === false}
            onClick={reloadAgentsData}
            title={t('humanResources.simulation.actualizeTable')}
        />
    );

    const isLoading =
        isHrSimulationAgentsResponseLoading ||
        isAgentDepartureHypothesesResponseLoading ||
        isAgentArrivalHypothesesResponseLoading ||
        isAgentPayComponentHypothesesResponseLoading ||
        isGradeAdvancementHypothesesResponseLoading;

    const isFetching =
        isHrSimulationAgentsResponseFetching ||
        isAgentDepartureHypothesesResponseFetching ||
        isAgentArrivalHypothesesResponseFetching ||
        isAgentPayComponentHypothesesResponseFetching ||
        isGradeAdvancementHypothesesResponseFetching;

    const footerActions = (
        <AddAgentButton
            hrSimulation={hrSimulation}
            isLoading={isLoading}
            recurrence={recurrence}
        />
    );

    return (
        <StyHrDataGrid
            title={t(`humanResources.simulation.agentGrid.${recurrence}`)}
            rowData={agentData}
            trackEventMetadata={{
                gridName:
                    recurrence === RecurrenceEnum.RECURRENT
                        ? TRACKABLE_EVENTS_METADATA.human_resources.fullScreen
                              .recurrentGrid
                        : TRACKABLE_EVENTS_METADATA.human_resources.fullScreen
                              .nonRecurrentAgentGrid,
            }}
            columnDefs={columnDefs}
            exportFileName={exportFileName}
            isLoading={isLoading}
            isFetching={isFetching}
            gridProps={gridProps}
            cardHeaderActions={headerActions}
            cardFooterActions={footerActions}
        />
    );
};

export default memo(AgentDataGrid);
